/**
 * ComandPage — Pannello comandi touch-friendly per iPad (Modalità PC+iPad)
 *
 * Si apre su: /comandi
 * Comunicazione con il PC via polling su /api/sync.php ogni 200ms
 *
 * - Riceve: stato timer dal PC (timerDisplay, timerRunning)
 * - Invia:  comandi video (play/pause) e aggiornamenti scores/timer
 * - Salva:  azioni e penalità sul backend API (stesso flusso di MainLayout)
 */

import React, { useRef, useCallback, useState, useEffect } from 'react';
import { MatchProvider, useMatch } from '../context/MatchContext';
import AthletePanel from './AthletePanel';
import ActionPanel from './ActionPanel';
import BiomechPanel from './BiomechPanel';
import OutcomePanel from './OutcomePanel';
import TimelinePanel from './TimelinePanel';
import { useTimer } from '../hooks/useTimer';
import { saveAction, savePenalty, deleteAction, deletePenalty, updateAction } from '../services/api';
import { calculatePoints } from '../utils/scoring';
import { formatDisplay } from '../hooks/useTimer';
import { getFlagByAlpha3 } from '../utils/tkdCountries';

const API_SYNC      = '/api/sync.php';
const API_BASE      = '/api';
const TIMER_POST_MS = 200;

// ── Componente interno (ha accesso a MatchContext) ──────────────────────────
function ComandPageContent() {
  const { state, loadSession, setTimer, setVideoTimestamp, setRound,
          confirmAction, deleteAction: ctxDeleteAction, updateAction: ctxUpdateAction,
          loadActionForEdit, clearEditMode,
          addPenalty, deletePenalty: ctxDeletePenalty } = useMatch();
  const { isSetupComplete, session } = state;

  const [initError, setInitError] = useState('');
  const commandIdRef = useRef(0);

  // ── Timer locale (iPad è il master del timer in Mode 3) ─────────────────
  const handleTimerTick = useCallback((s) => {
    setTimer({ seconds: s, display: formatDisplay(s) });
  }, [setTimer]);

  const timer = useTimer(handleTimerTick);

  // ── Invia comando video a sync.php ──────────────────────────────────────
  const postVideoCommand = useCallback((command, extra = {}) => {
    commandIdRef.current += 1;
    fetch(API_SYNC, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ command, commandId: commandIdRef.current, ...extra }),
    }).catch(() => {});
  }, []);

  // ── Pubblica stato timer su sync.php ogni TIMER_POST_MS ─────────────────
  useEffect(() => {
    if (!isSetupComplete) return;
    const iv = setInterval(() => {
      fetch(API_SYNC, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          timerSeconds: state.timer.seconds,
          timerDisplay: state.timer.display,
          timerRunning: timer.isRunning,
        }),
      }).catch(() => {});
    }, TIMER_POST_MS);
    return () => clearInterval(iv);
  }, [isSetupComplete, state.timer.seconds, state.timer.display, timer.isRunning]);

  // ── Pubblica scores/round quando cambiano ────────────────────────────────
  useEffect(() => {
    if (!isSetupComplete) return;
    fetch(API_SYNC, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ scores: state.scores, currentRound: state.currentRound }),
    }).catch(() => {});
  }, [isSetupComplete, state.scores, state.currentRound]);

  // ── Inizializzazione: polling sync.php finché non arriva la sessione ─────
  useEffect(() => {
    if (isSetupComplete) return;

    const init = async () => {
      try {
        const res    = await fetch(API_SYNC);
        const { data } = await res.json();
        if (!data?.session) return; // Nessuna sessione ancora

        let actions   = [];
        let penalties = [];

        if (data.session.id) {
          try {
            const [actsRes, pensRes] = await Promise.all([
              fetch(`${API_BASE}/actions.php?session_id=${data.session.id}`).then(r => r.json()),
              fetch(`${API_BASE}/penalties.php?session_id=${data.session.id}`).then(r => r.json()),
            ]);
            actions   = actsRes.data  || [];
            penalties = pensRes.data  || [];
          } catch { /* offline — usa array vuoti */ }
        }

        loadSession(data.session, actions, penalties);
        setInitError('');
      } catch (e) {
        setInitError('Errore di connessione al server');
      }
    };

    const iv = setInterval(init, 500);
    init();
    return () => clearInterval(iv);
  }, [isSetupComplete, loadSession]);

  // ── Schermata di attesa ──────────────────────────────────────────────────
  if (!isSetupComplete) {
    return (
      <div className="comand-waiting">
        <div className="comand-waiting-icon">📱</div>
        <h2 className="comand-waiting-title">Pannello Comandi</h2>
        <p className="comand-waiting-sub">In attesa della sessione dal PC…</p>
        <p className="comand-waiting-hint">
          Sul PC principale seleziona la modalità <strong>🖥📱 PC+iPad</strong>
          {' '}e completa il setup della sessione.
        </p>
        {initError && <p className="comand-waiting-error">{initError}</p>}
        <div className="comand-spinner" />
      </div>
    );
  }

  // ── VideoSync mock (usa sync.php invece di BroadcastChannel) ─────────────
  const videoSync = {
    getCurrentTime: () => 0,                        // Nessun video locale
    sendPause:  () => postVideoCommand('pause'),
    sendPlay:   () => postVideoCommand('play'),
    sendSeek:   (t) => postVideoCommand('seek',  { commandTime:  t }),
    sendSpeed:  (r) => postVideoCommand('speed', { commandSpeed: r }),
    registerPlayer: () => {},
    initDualChannel: () => {},
    closeDualChannel: () => {},
  };

  // ── Gestione azioni ──────────────────────────────────────────────────────
  const handleAthleteSelect = () => {
    timer.pause();
    videoSync.sendPause();
    setVideoTimestamp(0);
  };

  const handleConfirmAction = async (actionData) => {
    const pts = calculatePoints(
      actionData.technique, actionData.target,
      actionData.outcome, actionData.technicalBonus
    );
    const fullAction = {
      ...actionData,
      id: Date.now(),
      session_id: session?.id,
      round_number: state.currentRound,
      timestamp_video: 0,
      timestamp_display: formatDisplay(state.timer.seconds),
      points: pts,
    };

    confirmAction(fullAction);

    if (session?.id) {
      try {
        await saveAction({
          session_id:        session.id,
          round_number:      state.currentRound,
          timestamp_video:   0,
          timestamp_display: fullAction.timestamp_display,
          athlete:           fullAction.athlete,
          action_type:       fullAction.actionType,
          technique:         fullAction.technique,
          custom_technique:  fullAction.customTechnique || null,
          leg_side:          fullAction.legSide     || null,
          leg_position:      fullAction.legPosition || null,
          guard:             fullAction.guard       || null,
          distance:          fullAction.distance    || null,
          sequence_id:       fullAction.sequence_id || null,
          target:            fullAction.target,
          outcome:           fullAction.outcome,
          points:            pts,
          technical_bonus:   fullAction.technicalBonus ? 1 : 0,
        });
      } catch (e) { console.error('Errore salvataggio azione (iPad):', e); }
    }

    timer.start();
    videoSync.sendPlay();
  };

  const handleCancelAction = () => {
    timer.start();
    videoSync.sendPlay();
  };

  const handleDeleteAction = async (actionId) => {
    ctxDeleteAction(actionId);
    if (session?.id) {
      try { await deleteAction(actionId); } catch (e) { console.error(e); }
    }
  };

  const handleLoadForEdit = (action) => {
    loadActionForEdit(action);
  };

  const handleAddPenalty = async (penaltyData) => {
    const pen = {
      ...penaltyData,
      id: Date.now(),
      session_id: session?.id,
      round_number: state.currentRound,
      timestamp_video: 0,
      timestamp_display: formatDisplay(state.timer.seconds),
    };
    addPenalty(pen);
    if (session?.id) {
      try {
        await savePenalty({
          session_id:        session.id,
          round_number:      state.currentRound,
          timestamp_video:   0,
          timestamp_display: pen.timestamp_display,
          athlete:           pen.athlete,
          penalty_type:      pen.penalty_type,
        });
      } catch (e) { console.error(e); }
    }
  };

  const handleDeletePenalty = async (penId) => {
    ctxDeletePenalty(penId);
    if (session?.id) {
      try { await deletePenalty(penId); } catch (e) { console.error(e); }
    }
  };

  // ── Header compatto per iPad ─────────────────────────────────────────────
  const { scores, currentRound } = state;
  const numRounds = session?.num_rounds || 3;
  const blueName  = session?.athlete_blue_name || 'BLU';
  const redName   = session?.athlete_red_name  || 'ROSSO';
  const blueFlag  = getFlagByAlpha3(session?.athlete_blue_nationality);
  const redFlag   = getFlagByAlpha3(session?.athlete_red_nationality);

  return (
    <div className="app-container touch-mode">
      {/* Header compatto touch */}
      <header className="header comand-header">
        <div className="header-top">
          {/* Timer controls */}
          <div className="header-controls" style={{ flex: '0 0 auto' }}>
            <button
              className={`btn btn-lg ${timer.isRunning ? 'btn-warning' : 'btn-success'}`}
              onClick={timer.isRunning ? timer.pause : timer.start}
              title={timer.isRunning ? 'Pausa' : 'Avvia'}
            >
              {timer.isRunning ? '⏸' : '▶'}
            </button>
            <button className="btn btn-lg btn-secondary" onClick={timer.reset} title="Reset">
              ⏹
            </button>
          </div>

          {/* Scoreboard */}
          <div className="header-scoreboard">
            <span className="athlete-name blue">
              {blueFlag && <span style={{ marginRight: 4 }}>{blueFlag}</span>}
              {blueName}
            </span>
            <span className="score-display blue">{scores.blue}</span>
            <span className={`timer-display${timer.isRunning ? ' running' : ''}`}>
              {state.timer.display}
            </span>
            <span className="score-display red">{scores.red}</span>
            <span className="athlete-name red">
              {redName}
              {redFlag && <span style={{ marginLeft: 4 }}>{redFlag}</span>}
            </span>
          </div>

          {/* Round */}
          <div className="header-controls" style={{ flex: '0 0 auto' }}>
            <span className="text-dim" style={{ fontSize: 12, marginRight: 4 }}>R:</span>
            {Array.from({ length: numRounds }, (_, i) => i + 1).map(r => (
              <button
                key={r}
                className={`round-btn${currentRound === r ? ' active' : ''}`}
                onClick={() => setRound(r)}
                style={{ minWidth: 44, minHeight: 44 }}
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        {/* Score per round */}
        <div className="header-bottom" style={{ justifyContent: 'center' }}>
          {scores.byRound.map(r => (
            <span key={r.round} className="text-dim" style={{ fontSize: 12 }}>
              R{r.round}: <span className="text-blue fw-bold">{r.blue}</span>-<span className="text-red fw-bold">{r.red}</span>
            </span>
          ))}
          <span style={{ marginLeft: 8, color: 'var(--color-text-dim)', fontSize: 11 }}>📱 iPad</span>
        </div>
      </header>

      {/* Pannelli analisi a 5 colonne */}
      <div className="main-content" style={{ gridTemplateColumns: '1fr' }}>
        <div className="analysis-section">
          <div className="analysis-panel">
            <AthletePanel
              onAthleteSelect={handleAthleteSelect}
              onAddPenalty={handleAddPenalty}
              onDeletePenalty={handleDeletePenalty}
            />
            <ActionPanel />
            <BiomechPanel />
            <OutcomePanel onConfirm={handleConfirmAction} onCancel={handleCancelAction} />
            <TimelinePanel onDeleteAction={handleDeleteAction} onLoadForEdit={handleLoadForEdit} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Wrapper con MatchProvider dedicato ──────────────────────────────────────
function ComandPage() {
  return (
    <MatchProvider>
      <ComandPageContent />
    </MatchProvider>
  );
}

export default ComandPage;
